// Konto mit Supabase (supabase-js v2). Ohne Oberfläche, getestet mit einem nachgebauten Supabase (test/fake-supabase.js).
// Ablauf:
//  - Erster Start: anonyme Anmeldung (signInAnonymously). Das ist ein echtes Konto auf dem Server; alles wird ab der ersten Bewertung gesichert.
//  - Konto sichern: anonymes Konto per updateUser({ email }) umwandeln. Die Nutzer-ID bleibt, damit auch der Lernstand. Bestätigung per Link
//    oder per Code aus der Mail (verifyOtp, Typ email_change).
//  - Anmelden auf einem anderen Gerät: signInWithOtp (nur bestehende Konten), Bestätigung per Link oder Code (Typ email).
//    Was auf diesem Gerät anonym gelernt wurde, übernimmt core.js beim Kontowechsel (adoptUserData).
//  - Abmelden: signOut nur für dieses Gerät; danach startet wieder ein anonymes Konto.
// Captcha (Cloudflare Turnstile) ist optional: captcha.token() liefert ein Einmal-Token für anonyme Anmeldung und Login-Mail.
//  - Anmelden mit Google (oder einem anderen Anbieter aus PROVIDERS, freigeschaltet über config.authProviders): Ist das Konto anonym, wird der Anbieter
//    an dieses Konto gehängt (linkIdentity; Nutzer-ID und Lernstand bleiben). Gibt es zum Google-Konto oder zur Adresse schon ein Konto, kommt Supabase
//    mit identity_already_exists bzw. email_exists zurück; dann meldet start() dieses Konto per signInWithOAuth an, und core.js übernimmt den Gerätestand
//    (wie bei „Schon ein Konto?“). Ohne Konto auf dem Gerät gleich signInWithOAuth. Der Browser verlässt dabei die Seite; wohin es danach geht,
//    steht bis zur Rückkehr im sessionStorage (OAUTH_KEY).

export const PROVIDERS = { google: 'Google', apple: 'Apple' };
const OAUTH_KEY = 'sentenzo.oauth';
const OAUTH_FALLBACK = new Set(['identity_already_exists', 'email_exists', 'user_already_exists']);
const browserSession = () => {   // sessionStorage, falls vorhanden (privater Modus, Tests: dann im Speicher)
  const mem = new Map();
  const s = typeof sessionStorage !== 'undefined' ? sessionStorage : null;
  return {
    get: k => { try { return s ? s.getItem(k) : (mem.has(k) ? mem.get(k) : null); } catch { return mem.has(k) ? mem.get(k) : null; } },
    set: (k, v) => { try { if (s) s.setItem(k, v); else mem.set(k, v); } catch { mem.set(k, v); } },
    remove: k => { try { if (s) s.removeItem(k); } catch { /* egal */ } mem.delete(k); },
  };
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Fehlercode von Supabase. Kommt eine Antwort ohne Code (supabase-js liest ihn je nach API-Version aus code oder error_code), erkennen wir die
// wichtigen Fälle am Text; sonst fiele „bekannte Adresse → anmelden“ auf der Anmeldeseite aus und es bliebe nur ein Hinweis ohne Weg.
export function authCode(error) {
  const code = (error && (error.code || (error.error && error.error.code))) || '';
  if (code) return code;
  const msg = String((error && error.message) || '');
  return /already (been )?registered|already exists/i.test(msg) ? 'email_exists' : /signups not allowed/i.test(msg) ? 'otp_disabled' : '';
}

// Fehler von Supabase in verständliche Sätze übersetzen
export function describeAuthError(error, ctx = '') {
  const code = authCode(error);
  const msg = String((error && error.message) || error || '');
  const status = error && error.status;
  if (code === 'email_exists' || code === 'user_already_exists' || /already (been )?registered|already exists/i.test(msg))
    return 'Zu dieser Adresse gibt es schon ein Konto. Melde dich unter „Schon ein Konto?“ an; was du hier gelernt hast, wird übernommen.';
  if (code === 'otp_disabled' || code === 'signup_disabled' || /signups not allowed/i.test(msg))
    return ctx === 'signin' ? 'Zu dieser Adresse gibt es noch kein Konto. Sichere dein jetziges Konto unter „Konto sichern“ mit dieser Adresse.' : 'Neue Konten sind auf dem Server gerade ausgeschaltet.';
  if (code === 'over_email_send_rate_limit' || code === 'over_request_rate_limit' || status === 429) return 'Zu viele Versuche. Bitte warte ein paar Minuten.';
  if (code === 'otp_expired' || /expired|invalid.*(otp|token)/i.test(msg)) return 'Der Code oder Link ist abgelaufen oder falsch. Bitte fordere einen neuen an.';
  if (code === 'captcha_failed' || /captcha/i.test(msg)) return 'Die Sicherheitsprüfung hat nicht geklappt. Bitte versuche es noch einmal.';
  if (code === 'anonymous_provider_disabled') return 'Lernen ohne Anmeldung ist auf dem Server ausgeschaltet.';
  if (code === 'email_address_invalid' || code === 'validation_failed') return 'Bitte prüfe die E-Mail-Adresse.';
  if (code === 'PGRST202' || code === '42883') return 'Diese Funktion ist auf dem Server noch nicht eingerichtet. Bitte versuche es später noch einmal.';
  if (code === 'bad_code_verifier' || code === 'flow_state_not_found') return ctx === 'oauth' ? 'Die Anmeldung wurde in einem anderen Browser beendet. Bitte versuche es noch einmal.' : 'Der Link wurde in einem anderen Browser geöffnet. Bitte gib stattdessen den Code aus der Mail ein.';
  if (code === 'provider_disabled' || /provider is not enabled|Unsupported provider/i.test(msg)) return 'Diese Anmeldung ist auf dem Server noch nicht freigeschaltet. Bitte nutze vorerst die E-Mail-Adresse.';
  if (code === 'manual_linking_disabled') return 'Das Verknüpfen von Konten ist auf dem Server noch ausgeschaltet. Bitte nutze vorerst die E-Mail-Adresse.';
  if (code === 'identity_already_exists') return 'Dieses Konto ist schon mit einem anderen Sentenzo-Konto verbunden. Melde dich damit an.';
  if (/access_denied/.test(code) || /access.denied|denied access/i.test(msg)) return 'Die Anmeldung wurde abgebrochen.';
  if (isNetworkError(error)) return 'Keine Verbindung zum Server. Bitte versuche es mit Internet noch einmal.';
  return `Das hat nicht geklappt (${code || msg || 'unbekannter Fehler'}).`;
}

export function isNetworkError(error) {
  if (!error) return false;
  const name = error.name || '', msg = String(error.message || error);
  return name === 'AuthRetryableFetchError' || error.status === 0 || /fetch|network|NetworkError|Load failed|ECONN|timeout/i.test(msg);
}

const pick = u => u ? { id: u.id, email: u.email || null, new_email: u.new_email || null, is_anonymous: !!u.is_anonymous, providers: (u.app_metadata && u.app_metadata.providers) || [] } : null;   // providers: für die Nutzungsdaten (E-Mail oder Google)

export class Account {
  constructor({ client, captcha = null, redirectTo = '', onChange = () => {}, session = browserSession() }) {
    Object.assign(this, { client, captcha, redirectTo, onChange, session });
    this.user = null; this.error = null; this.urlError = null; this._pending = null;
    this.oauthRetry = null; this.oauthBack = null;   // nach der Rückkehr von Google: erneut als Anmeldung versuchen bzw. Ziel in der App
  }

  async captchaToken() {
    if (!this.captcha) return undefined;
    try { return await this.captcha.token(); } catch { return undefined; }   // der Server lehnt dann mit captcha_failed ab, das wird gemeldet
  }

  async start() {
    // Rücksprung aus einer Mail (?code=… bzw. ?error=…): supabase-js tauscht den Code beim Start selbst; Fehler hier sichtbar machen
    if (this.client.auth.initialize) {
      const r = await this.client.auth.initialize();
      if (r && r.error) this.urlError = describeAuthError(r.error);
    }
    if (typeof location !== 'undefined') {
      const q = new URLSearchParams(location.search), h = new URLSearchParams(location.hash.replace(/^#/, '').split('?').pop());
      const desc = q.get('error_description') || h.get('error_description'), code = q.get('error_code') || h.get('error_code') || q.get('error') || '';
      const pending = this._oauthTake();
      if (desc || code) {
        if (pending && pending.step === 'link' && OAUTH_FALLBACK.has(code)) { this.oauthRetry = { provider: pending.provider, back: pending.back }; this.urlError = null; }   // Konto gibt es schon: dort anmelden
        else this.urlError = describeAuthError({ code, message: desc }, pending ? 'oauth' : '');
        if (typeof history !== 'undefined') history.replaceState(null, '', (this.oauthRetry ? (pending.back || '/') : '/konto'));
      } else if (pending) this.oauthBack = pending.back || null;
    }
    // Rückruf nicht direkt verarbeiten: supabase-js warnt vor weiteren Aufrufen innerhalb des Rückrufs (Verklemmung)
    this.client.auth.onAuthStateChange((event, session) => { setTimeout(() => this._set(session ? session.user : null, event), 0); });
    const { data } = await this.client.auth.getSession();
    if (data && data.session) this._set(data.session.user, 'INITIAL_SESSION');
    else await this.ensureUser();
    return this.user;
  }

  // Ohne Konto: anonym anmelden. Schlägt das fehl (offline), bleibt die App lokal und versucht es später wieder (core.js).
  ensureUser() {
    if (this.user) return Promise.resolve(this.user);
    if (this._pending) return this._pending;
    this._pending = (async () => {
      const captchaToken = await this.captchaToken();
      const { data, error } = await this.client.auth.signInAnonymously(captchaToken ? { options: { captchaToken } } : undefined);
      if (error) { this.error = describeAuthError(error); this.onChange('ERROR', this.user, this.user); return null; }
      this.error = null;
      this._set(data.user || (data.session && data.session.user), 'SIGNED_IN');
      return this.user;
    })().finally(() => { this._pending = null; });
    return this._pending;
  }

  _set(rawUser, event) {
    const prev = this.user, next = pick(rawUser);
    if (event === 'SIGNED_OUT' || !next) { if (prev && event === 'SIGNED_OUT') { this.user = null; this.onChange(event, prev, null); } return; }
    this.user = next;
    if (!prev || prev.id !== next.id || prev.is_anonymous !== next.is_anonymous || prev.email !== next.email) this.onChange(event, prev, next);
  }

  // Nach einer Bestätigung in einem anderen Browser: Konto neu vom Server lesen (z. B. beim Zurückkehren in die App)
  async refreshUser() {
    if (!this.user) return null;
    const { data, error } = await this.client.auth.getUser();
    if (!error && data && data.user) this._set(data.user, 'USER_UPDATED');
    return this.user;
  }

  async secure(email) {
    email = String(email || '').trim().toLowerCase();
    if (!EMAIL_RE.test(email)) return { ok: false, message: 'Bitte gib eine gültige E-Mail-Adresse ein.' };
    if (!this.user && !(await this.ensureUser())) return { ok: false, message: this.error || 'Keine Verbindung zum Server.' };
    if (!this.user.is_anonymous) return { ok: false, message: 'Dein Konto ist schon gesichert.' };
    const { error } = await this.client.auth.updateUser({ email }, { emailRedirectTo: this.redirectTo });
    if (error) return { ok: false, message: describeAuthError(error, 'secure'), code: authCode(error) };
    return { ok: true, email };
  }

  async signIn(email) {
    email = String(email || '').trim().toLowerCase();
    if (!EMAIL_RE.test(email)) return { ok: false, message: 'Bitte gib eine gültige E-Mail-Adresse ein.' };
    const captchaToken = await this.captchaToken();
    const { error } = await this.client.auth.signInWithOtp({ email, options: { shouldCreateUser: false, emailRedirectTo: this.redirectTo, ...(captchaToken ? { captchaToken } : {}) } });
    if (error) return { ok: false, message: describeAuthError(error, 'signin'), code: authCode(error) };
    return { ok: true, email };
  }

  // Code aus der Mail: type 'email_change' (Konto sichern) oder 'email' (Anmelden)
  async confirm(email, token, type) {
    token = String(token || '').replace(/\s+/g, '');
    if (!/^\d{6,10}$/.test(token)) return { ok: false, message: 'Bitte gib den Code aus der Mail ein (nur Ziffern).' };
    const { data, error } = await this.client.auth.verifyOtp({ email, token, type });
    if (error) return { ok: false, message: describeAuthError(error, type === 'email' ? 'signin' : 'secure'), code: authCode(error) };
    this._set(data.user || (data.session && data.session.user), type === 'email' ? 'SIGNED_IN' : 'USER_UPDATED');
    return { ok: true };
  }

  // Anmelden mit einem Anbieter (Google …). Ergebnis { ok: true }, danach verlässt der Browser die Seite; Rückkehr nach redirectTo.
  // signIn: true erzwingt eine Anmeldung statt des Verknüpfens (zweiter Schritt, wenn das Konto schon existiert).
  async oauth(provider, { back = '', signIn = false } = {}) {
    if (!PROVIDERS[provider]) return { ok: false, message: 'Diese Anmeldung gibt es nicht.' };
    const link = !signIn && !!(this.user && this.user.is_anonymous);
    this.session.set(OAUTH_KEY, JSON.stringify({ provider, step: link ? 'link' : 'signin', back }));
    const opts = { provider, options: { redirectTo: this.redirectTo } };
    let res;
    try { res = link ? await this.client.auth.linkIdentity(opts) : await this.client.auth.signInWithOAuth(opts); }
    catch (e) { res = { error: e }; }
    if (res && res.error) { this.session.remove(OAUTH_KEY); return { ok: false, message: describeAuthError(res.error, 'oauth'), code: res.error.code }; }
    return { ok: true, link };
  }

  _oauthTake() {
    const j = this.session.get(OAUTH_KEY); if (!j) return null;
    this.session.remove(OAUTH_KEY);
    try { return JSON.parse(j); } catch { return null; }
  }

  async signOut() {
    const { error } = await this.client.auth.signOut({ scope: 'local' });
    if (error) return { ok: false, message: describeAuthError(error) };
    const prev = this.user; this.user = null;
    if (prev) this.onChange('SIGNED_OUT', prev, null);
    return { ok: true };
  }

  // Konto endgültig löschen (RPC delete_my_account, Migration 20261017000000). Danach gibt es die Sitzung auf dem Server nicht mehr;
  // sie wird nur noch auf dem Gerät beendet (Fehler dabei sind egal, das Konto ist schon weg).
  async deleteAccount() {
    if (!this.user) return { ok: false, message: 'Du bist nicht angemeldet.' };
    const { error } = await this.client.rpc('delete_my_account');
    if (error) return { ok: false, message: describeAuthError(error) };
    try { await this.client.auth.signOut({ scope: 'local' }); } catch { /* egal */ }
    const prev = this.user; this.user = null;
    this.onChange('SIGNED_OUT', prev, null);
    return { ok: true };
  }

  async exportData() {
    const { data, error } = await this.client.rpc('export_my_data');
    if (error) return { ok: false, message: describeAuthError(error) };
    return { ok: true, data };
  }
}
